// src/components/time-tracking/ClockInOutButton.jsx
// Presentation component for clock in/out. All state lives in TimeTrackingContext.

import { useState, useEffect } from 'react';
import { Car, Clock, Loader2, LogIn, X } from 'lucide-react';
import { useTimeTracking } from '@/contexts/TimeTrackingContext';
import { useUser } from '@/contexts/UserContext';
import { apiService } from '@/services/api';
import { cn } from '@/lib/utils';
import { buildSuggestedAllocations, shiftDateStr } from '@/utils/timeTracking';
import { timestampToDateOnly } from '@/utils/dateHelpers';

function formatElapsed(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export function ClockInOutButton({ compact = false }) {
  const { visible, activeEntry, elapsed, loading, trackMileage, clockIn, clockOut } = useTimeTracking();
  const { user } = useUser();
  const [actionLoading, setActionLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [clockOutNotes, setClockOutNotes] = useState('');
  const [clockOutMiles, setClockOutMiles] = useState('');
  const [allocations, setAllocations] = useState([]);
  const [allocationsLoading, setAllocationsLoading] = useState(false);

  // Fetch today's schedule overrides and org config when clock-out modal opens
  useEffect(() => {
    if (!showModal || !activeEntry || !user?.id) return;
    let cancelled = false;
    setAllocationsLoading(true);

    // Local date (not UTC): an Eastern clock-out after 8pm must still look at today's blocks
    const today = timestampToDateOnly(Date.now());
    Promise.all([
      apiService.getScheduleOverrides(user.id, today, today),
      apiService.getOrganization(user.organization_id),
      // Other entries around today: the time-entry list bounds by UTC day, so over-fetch
      // a day either side and let the helper filter by local date
      apiService.getTimeEntries({ user_id: user.id, start_date: shiftDateStr(today, -1), end_date: shiftDateStr(today, 1), per_page: 100 })
        .catch(() => [])
    ]).then(([overrides, org, entriesRes]) => {
      if (cancelled) return;
      const tt = org?.settings?.time_tracking || {};
      const existingEntries = Array.isArray(entriesRes) ? entriesRes : (entriesRes?.items || []);
      setAllocations(buildSuggestedAllocations({
        overrides,
        existingEntries,
        dateStr: today,
        orgTimeTracking: tt,
        userPayRates: user.pay_rates || {},
        excludeEntryId: activeEntry.id,
      }));
    }).catch(() => {
      if (!cancelled) setAllocations([]);
    }).finally(() => {
      if (!cancelled) setAllocationsLoading(false);
    });

    return () => { cancelled = true; };
  }, [showModal, activeEntry, user?.id, user?.organization_id, user?.pay_rates]);

  if (loading || !visible) return null;

  const handleClockIn = async () => {
    setActionLoading(true);
    try {
      await clockIn();
    } catch (err) {
      console.error('Clock in failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    setActionLoading(true);
    try {
      const miles = clockOutMiles ? parseFloat(clockOutMiles) : undefined;
      const filtered = allocations.filter(a => a.minutes > 0);
      await clockOut(clockOutNotes || undefined, miles, filtered.length > 0 ? filtered : undefined);
      setClockOutNotes('');
      setClockOutMiles('');
      setAllocations([]);
      setShowModal(false);
    } catch (err) {
      console.error('Clock out failed:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Clocked in state
  if (activeEntry) {
    return (
      <>
        <button
          onClick={() => setShowModal(true)}
          disabled={actionLoading}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg text-sm font-medium transition-colors',
            'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200',
            compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5'
          )}
        >
          <Clock className={cn('animate-pulse', compact ? 'h-3 w-3' : 'h-4 w-4')} />
          <span className="tabular-nums">{formatElapsed(elapsed)}</span>
        </button>

        {/* Clock Out Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowModal(false)} />
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6 max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Clock Out</h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="overflow-y-auto min-h-0 flex-1">
                <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
                  <Clock className="h-5 w-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-500">Time elapsed</p>
                    <p className="text-xl font-semibold tabular-nums text-gray-900">{formatElapsed(elapsed)}</p>
                  </div>
                </div>

                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea
                  value={clockOutNotes}
                  onChange={(e) => setClockOutNotes(e.target.value)}
                  placeholder="What did you work on?"
                  rows={3}
                  className="w-full text-sm border border-gray-300 rounded-lg p-3 mb-4 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                />

                {/* Hour Allocations */}
                {allocationsLoading && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading time blocks...
                  </div>
                )}
                {!allocationsLoading && allocations.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Time Allocations</label>
                    <div className="space-y-2">
                      {allocations.map((alloc, idx) => (
                        <div key={alloc.reason} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{alloc.reason}</p>
                            {alloc.pay_code && (
                              <p className="text-xs text-gray-500">{alloc.pay_code} {alloc.rate > 0 ? `· $${alloc.rate}/hr` : ''}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              inputMode="numeric"
                              value={alloc.minutes}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                setAllocations(prev => prev.map((a, i) =>
                                  i === idx ? { ...a, minutes: isNaN(val) ? 0 : val } : a
                                ));
                              }}
                              className="w-16 text-sm text-right border border-gray-300 rounded-md p-1.5 focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                            <span className="text-xs text-gray-500">min</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {trackMileage && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Car className="inline h-4 w-4 mr-1 -mt-0.5" />
                      Miles Driven Today (optional)
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={clockOutMiles}
                      onChange={(e) => setClockOutMiles(e.target.value)}
                      placeholder="0.0"
                      className="w-full text-sm border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg py-2.5 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClockOut}
                  disabled={actionLoading || allocationsLoading}
                  className="flex-1 bg-red-600 text-white text-sm font-medium rounded-lg py-2.5 hover:bg-red-700 disabled:opacity-50"
                >
                  {actionLoading ? 'Clocking out...' : 'Clock Out'}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Clocked out state
  return (
    <button
      onClick={handleClockIn}
      disabled={actionLoading}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg text-sm font-medium transition-colors',
        'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200',
        compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5',
        actionLoading && 'opacity-50'
      )}
    >
      <LogIn className={compact ? 'h-3 w-3' : 'h-4 w-4'} />
      {actionLoading ? 'Clocking in...' : 'Clock In'}
    </button>
  );
}
